import React, { memo, useCallback } from 'react'

import {
  Button,
  Icon,
  Tooltip,
  BallModelBaseColor,
  BallModelClay,
  BallModelMetalness,
  BallModelNormals,
  BallModelPbr,
  BallModelRoughness,
  BallModelWireframe,
  CubeDraft,
}
from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { useViewerStore } from 'Pods/ThreeDStudio/Hooks/useViewerStore'
import { useViewerContext } from 'Pods/ThreeDStudio/Shared/ViewerContext/ViewerContext'

const buttons = [
  {
    id: 'pbr',
    label: 'PBR (M)',
    icon: BallModelPbr,
  },
  {
    id: 'clay',
    label: 'Clay (C to cycle)',
    icon: BallModelClay,
  },
  {
    id: 'basecolor',
    label: 'Base color only (C to cycle)',
    icon: BallModelBaseColor,
  },
  {
    id: 'roughness',
    label: 'Roughness only (C to cycle)',
    icon: BallModelRoughness,
  },
  {
    id: 'normals',
    label: 'Normals only (C to cycle)',
    icon: BallModelNormals,
  },
  {
    id: 'metalness',
    label: 'Metalness only (C to cycle)',
    icon: BallModelMetalness,
  },
  {
    id: 'segmentation',
    label: 'Segmentation (C to cycle)',
    icon: CubeDraft,
  },
]

function RenderModeSelector() {
  const viewer = useViewerContext()
  const renderMode = useViewerStore(viewer, "renderMode")
  const wireframe = useViewerStore(viewer, "wireframe")

  const setRenderMode = useCallback(
    (mode) => {
      viewer?.setRenderModeSettings({ mode })
    },
    [viewer],
  )

  const updateWireFrameSettings = useCallback(
    (value) => {
      viewer?.setWireframeSettings(value)
    },
    [viewer],
  )

  if (!viewer || (!renderMode && !wireframe)) {
    return null
  }

  return (
    <div className="texture-selector">
      {renderMode && buttons
        // Segmentation is only offered when the opened model actually supports it
        // (the viewer adds it to availableModes once the mesh has segments).
        .filter(({ id }) => id !== 'segmentation' || renderMode.availableModes.has('segmentation'))
        .map(({ id, label, icon }) => (
        <Tooltip content={label} key={id} id={`render-mode-${id}-tooltip`} place="bottom" >
          <Button
            type="ghost"
            variant="only-icon"
            size="tiny"
            icon={(
              <Icon
                svgComponent={icon}
                color={renderMode.mode === id ? 'primary' : 'darker'}
                notSpaced
              />
            )}
            isActive={renderMode.mode === id}
            disabled={!renderMode.availableModes.has(id)}
            onClick={() => setRenderMode(id)}
          />
        </Tooltip>
      ))}
      {renderMode && wireframe && <div className="texture-selector__divider" />}
      {wireframe && (
        <Tooltip content="Wireframe (W)" id="render-mode-wireframe-tooltip" place="bottom" >
          <Button
            type="ghost"
            variant="only-icon"
            size="tiny"
            isActive={wireframe.visible}
            onClick={() => updateWireFrameSettings({ visible: !wireframe.visible })}
            icon={(
              <Icon
                svgComponent={BallModelWireframe}
                color={wireframe.visible ? 'primary' : 'dark'}
                notSpaced
              />
            )}
          />
        </Tooltip>
      )}
    </div>
  )
}

export default memo(RenderModeSelector)
